import React from 'react';

import { useIntl } from 'react-intl';

import { LIST_GATEWAYS_RESOURCE } from 'src/features/gateway/api/useListGateways';
import { useUpdateGateway } from 'src/features/gateway/api/useUpdateGateway';
import GatewayFormModal from 'src/features/gateway/components/form/GatewayFormModal';
import { gatewayMessages } from 'src/features/gateway/utils/messages';
import { Gateway } from 'src/features/gateway/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface UpdateGatewayFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedGateway: Gateway;
    children?: React.ReactNode;
}

function UpdateGatewayFormModal<TData>({
    className,
    semantics,
    children,
    selectedGateway,
}: UpdateGatewayFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdateGateway({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_GATEWAYS_RESOURCE] });
            toast({
                title: formatMessage(gatewayMessages.updateGatewaySuccess),
                description: formatMessage(gatewayMessages.updateGatewaySuccessDescription, {
                    entry: success.translations.find((translation) => translation.language === locale)?.name,
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <GatewayFormModal
            selectedGateway={selectedGateway}
            className={className}
            mutate={(data, onSuccess) => mutate({ data, gatewayId: selectedGateway.id, parkingId }, { onSuccess })}
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(gatewayMessages.editGatewaySubtitle, {
                entry: selectedGateway.translations.find((translation) => translation.language === locale)?.name,
            })}
        >
            {children}
        </GatewayFormModal>
    );
}

export default UpdateGatewayFormModal;
